import { useState } from 'react';
import { CheckCircle2, Info, Play } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { useInboxes } from '@/hooks/use-inboxes';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { useAtomValue } from 'jotai';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { pulseOnboardingService } from '@/services/pulse-onboarding.service';
import { helpdeskLabel, isProviderInbox } from '@/constants/helpdesk-labels';
import { cn } from '@/lib/utils';
import type { BusinessUnit, Inbox } from '@/types';

/** "a", "a and b", "a, b and c" - names read better than a count. */
const list = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * What pressing the button will actually do, said before it is pressed.
 *
 * THIS REPLACED A GATE, deliberately. The old version refused to let the import start
 * until every active brand had a routed inbox, which read as safety but was not: the
 * expensive mistake is a DESTINATION nobody mapped, and an account can satisfy
 * "every brand has an inbox" with one of those still outstanding. So it blocked the
 * cheap case, a brand created a day before its inbox, and waved through the costly one.
 *
 * The operator is the only party who knows whether the mapping is finished, so the job
 * here is to tell them what the current mapping will produce and let them decide. The
 * only hard stop left is an account with no active brand, where there is nothing to
 * attribute a ticket to at all.
 *
 * Everything below is derived from the two queries the brand table already renders from,
 * so this line can never disagree with the rows beneath it, and it re-reads itself the
 * moment an inbox is mapped - the inbox mutations invalidate both queries.
 *
 * THE PROVIDER INBOX IS NOT EVIDENCE OF ROUTING. The `<provider>-inbox` a helpdesk
 * connect provisions is stamped with the default brand and carries no destination by
 * design, so counting it would mark the default brand routed the instant a helpdesk
 * connected, which is the state worth distinguishing.
 */
type Forecast = { blocked: string | null; summary: string };

const forecast = (units: BusinessUnit[], inboxes: Inbox[]): Forecast => {
  const active = units.filter((unit) => unit.status === 'active');
  if (active.length === 0) {
    return {
      blocked: 'This account has no active brands, so there is nothing to file tickets under.',
      summary: ''
    };
  }

  // Where anything unmatched lands: the brand the import itself resolves to.
  const fallback = active.find((unit) => unit.is_default) ?? active[0];

  const isRouted = (unit: BusinessUnit) =>
    inboxes.some(
      (inbox) =>
        inbox.bu_id === unit.id &&
        inbox.destination != null &&
        !isProviderInbox(inbox.name)
    );

  // One brand is the whole account: every ticket belongs to it however it arrived.
  if (active.length === 1) {
    return {
      blocked: null,
      summary: `Every ticket will be filed under ${fallback.name}, the only brand on this account.`
    };
  }

  const unrouted = active.filter((unit) => !isRouted(unit));
  // An inbox that carries traffic but belongs to no brand. One with no destination
  // routes nothing, so it is not a gap and does not belong in this sentence.
  const orphaned = inboxes.filter(
    (inbox) =>
      inbox.bu_id == null &&
      inbox.destination != null &&
      !isProviderInbox(inbox.name)
  );

  const parts = [
    `${active.length - unrouted.length} of ${active.length} brands have a routed inbox.`
  ];
  // Naming them adds nothing when the count already said "0 of 3".
  if (unrouted.length > 0 && unrouted.length < active.length) {
    parts.push(
      `${list(unrouted.map((unit) => unit.name))} ${unrouted.length === 1 ? 'has' : 'have'} none.`
    );
  }
  if (orphaned.length > 0) {
    parts.push(
      `${list(orphaned.map((inbox) => inbox.name))} ${orphaned.length === 1 ? 'is' : 'are'} not in a brand.`
    );
  }
  parts.push(
    unrouted.length > 0 || orphaned.length > 0
      ? `Tickets that do not match a mapped destination will be filed under ${fallback.name}, the default brand.`
      : 'Tickets will be filed by the inbox they arrived on.'
  );

  return { blocked: null, summary: parts.join(' ') };
};

/**
 * Starts the Pulse ticket import by hand.
 *
 * Reads the same two queries the brand table already loads, so the blur state costs no
 * extra request and can never disagree with the inbox rows rendered beside it.
 */
export function PulseOnboardingButton() {
  const accountId = useAtomValue(selectedAccountIdAtom);
  const { data: units = [], isLoading: unitsLoading } = useBusinessUnits();
  const { data: inboxes = [], isLoading: inboxesLoading } = useInboxes();
  const [busy, setBusy] = useState(false);
  // What the last press answered, kept on screen. A toast is gone in seconds and takes
  // the only evidence with it, which leaves the page looking exactly as it did before
  // the press — the state this is here to distinguish.
  //
  // Deliberately NOT a live progress reading. Nothing here knows when an import
  // finishes: that is `onboarding_status` on Pulse's own document, and polling for it
  // would be a second endpoint and a timer to maintain for a number an operator can see
  // in Pulse itself. This reports the one thing this screen actually knows — that the
  // import was accepted, and with what.
  const [outcome, setOutcome] = useState<
    { kind: 'started' | 'skipped'; text: string } | null
  >(null);

  const loading = unitsLoading || inboxesLoading;
  const { blocked, summary } = loading
    ? { blocked: null, summary: 'Reading the mapping…' }
    : forecast(units, inboxes);

  const start = async () => {
    if (accountId == null) return;
    setBusy(true);
    try {
      const result = await pulseOnboardingService.start(accountId);

      // No brand COUNT here, deliberately. `brands_configured` counts only the brands
      // choco copied — the non-default ones — because the default brand's configuration
      // is written by jobs as part of the import itself. On a two-brand account that
      // reads "Configured 1 brand", which looks like half the job was done when both
      // were. The per-brand state belongs on the Business units tab, which shows it
      // brand by brand; this line is about the import.
      if (result.status === 'skipped') {
        const text = `No import started: ${result.reason ?? 'no reason given'}.`;
        setOutcome({ kind: 'skipped', text });
        toast.warning(text);
      } else {
        const count = result.ticket_count?.toLocaleString() ?? 'The';
        const text = `Importing ${count} tickets from ${helpdeskLabel(result.platform)}.`;
        setOutcome({ kind: 'started', text });
        toast.success(text);
      }
    } catch (err) {
      const text =
        err instanceof Error ? err.message : 'Could not start the import';
      setOutcome(null);
      toast.error(text);
    } finally {
      setBusy(false);
    }
  };

  const button = (
    <Button
      size='sm'
      variant='outline'
      disabled={busy || loading || blocked != null}
      onClick={start}
      className='gap-1.5'
    >
      <Play className='size-3.5' />
      {busy ? 'Starting…' : outcome ? 'Start again' : 'Start Pulse import'}
    </Button>
  );

  // `role="status"` rather than an alert: it is the result of something the operator
  // just did, so a screen reader should read it when it settles and not interrupt.
  const result = outcome && (
    <p
      role='status'
      className={cn(
        'flex items-start gap-1.5 text-xs',
        outcome.kind === 'started' ? 'text-foreground' : 'text-muted-foreground'
      )}
    >
      {outcome.kind === 'started' ? (
        <CheckCircle2 className='mt-0.5 size-3.5 shrink-0 text-emerald-600' />
      ) : (
        <Info className='mt-0.5 size-3.5 shrink-0' />
      )}
      <span>{outcome.text}</span>
    </p>
  );

  return (
    <div className='space-y-2'>
      {button}
      {outcome ? (
        result
      ) : (
        // Not a tooltip. The whole point is that the operator reads this BEFORE
        // deciding, and a tooltip is only found by someone already suspicious;
        // it is also absent on touch, and it existed before only because a
        // disabled button fires no pointer events.
        <p
          className={cn(
            'max-w-md text-xs',
            blocked ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {blocked ?? summary}
        </p>
      )}
    </div>
  );
}
